import { PersonalDetailsFormProps } from '@/src/components/common/forms/personalDetailsForm'
import { travelersTypeTostringMap } from '@/src/constants/travelers'
import {
    DocumentDetails,
    DocumentType,
    NonDocumentField,
    Passenger,
    PassengerType,
    Traveler
} from '@/src/types/itinerary'
import { isoStringToDateObject } from '@/src/utils/itinerary/dateTransformer'
import { genderToTitleMap } from '@/src/utils/itinerary/genderToTitleMap'
import { getLocalSavedTravelerData } from '@/src/utils/itinerary/travelerDataLocalStoreManager'
import * as TravelerDocumentUtils from '@/src/utils/itinerary/travelerDocumentToInputsSegregater'
import { useCallback, useMemo, useState } from 'react'
import useGetItineraryId from '../../useGetItineraryId'

export const useTravelerState = ({
    mandatoryFields,
    passengers,
    isNationalityRequiredToRenderInUserPersonalDetailsSection,
    isDateOfBirthRequired,
    requiredDocuments,
    isBirthCountryRequired
}: {
    mandatoryFields: (DocumentType | NonDocumentField)[]
    passengers: Passenger[]
    isNationalityRequiredToRenderInUserPersonalDetailsSection: boolean
    isDateOfBirthRequired: boolean
    requiredDocuments: DocumentType[]
    isBirthCountryRequired: boolean
}) => {
    const itineraryId = useGetItineraryId()

    const travelersIndexToPassengerTypeAndSerialNumberMap: Record<
        number,
        { type: PassengerType; count: number; typeString: string }
    > = useMemo(() => {
        const typeCounts: Record<PassengerType, number> = {} as Record<PassengerType, number>

        return passengers.reduce(
            (acc, passenger, index) => {
                const passengerType = passenger.type
                const passengerTypeString = travelersTypeTostringMap[passengerType].toLowerCase()

                // Initialize count for this passenger type if not exists
                if (!typeCounts[passengerType]) {
                    typeCounts[passengerType] = 0
                }

                // Increment count for this passenger type
                typeCounts[passengerType] += 1

                acc[index] = {
                    type: passengerType,
                    typeString: passengerTypeString,
                    count: typeCounts[passengerType]
                }
                return acc
            },
            {} as Record<number, { type: PassengerType; count: number; typeString: string }>
        )
    }, [passengers])

    const localTravelerData = useMemo(() => {
        return getLocalSavedTravelerData()
    }, [])

    //we will local storage traveler data if it is not same as current itinerary id, because for  same itinerary id BE will send same travelers data
    const filledWithLocalSavedData = useMemo(() => {
        if (
            JSON.stringify(travelersIndexToPassengerTypeAndSerialNumberMap) ===
                JSON.stringify(localTravelerData?.travelersIndexToPassengerTypeAndSerialNumberMap) &&
            localTravelerData?.travelers.length === passengers.length &&
            localTravelerData?.itineraryId !== itineraryId
        ) {
            return true
        }
        //clear local storage
        // clearLocalSavedTravelerData(itineraryId)
        return false
    }, [travelersIndexToPassengerTypeAndSerialNumberMap, localTravelerData, passengers])

    // Initialize travelers state based on passengers and mandatory fields
    const initialTravelersState = useMemo((): Traveler[] => {
        return passengers?.map((passenger: Passenger, index: number) => {
            const targetSavedTraveler = localTravelerData?.travelers[index]!

            const traveler: Traveler = {
                id: filledWithLocalSavedData ? targetSavedTraveler.id || null : passenger.peopleId || null,
                firstName: filledWithLocalSavedData ? targetSavedTraveler.firstName || '' : passenger.firstName || '',
                lastName: filledWithLocalSavedData ? targetSavedTraveler.lastName || '' : passenger.lastName || '',
                title: filledWithLocalSavedData
                    ? targetSavedTraveler.title || ''
                    : passenger.title || genderToTitleMap(passenger.gender, passenger.type) || '',
                paxType: passenger.type,
                documentDetails: {} as Record<DocumentType, DocumentDetails>,
                showError: false,
                showSuccess: false,
                domId: `traveler-${index}`
            }

            // Add optional fields based on mandatoryFields
            if (isNationalityRequiredToRenderInUserPersonalDetailsSection) {
                traveler.nationality = filledWithLocalSavedData
                    ? targetSavedTraveler.nationality || ''
                    : passenger.nationality || ''
            }
            if (isBirthCountryRequired) {
                traveler.birthCountry = filledWithLocalSavedData
                    ? targetSavedTraveler.birthCountry || ''
                    : passenger.birthCountry || ''
            }
            if (isDateOfBirthRequired) {
                traveler.dateOfBirth = filledWithLocalSavedData
                    ? targetSavedTraveler?.dateOfBirth || { day: '', month: '', year: '' }
                    : isoStringToDateObject(passenger.dob) || { day: '', month: '', year: '' }
            }
            //if multiple document type available we will show all of them with radio button and user can select one of them
            traveler.documentDetails = requiredDocuments.reduce(
                (acc, documentType) => {
                    const documentInputs = Object.keys(
                        TravelerDocumentUtils.travelerDocumentToInputsSegregate(documentType)
                    ) as (keyof DocumentDetails)[]
                    //find if passenger has document details for this document type
                    const passengerDocumentDetails = passenger?.docDetails?.find(doc => doc?.docType === documentType)
                    acc[documentType] = documentInputs.reduce(
                        (acc, input) => {
                            if (input === 'docType') {
                                acc[input] = documentType
                            } else if (input === 'expiryDate' || input === 'issueDate') {
                                acc[input] = filledWithLocalSavedData
                                    ? targetSavedTraveler?.documentDetails?.[documentType]?.[input] || {
                                          day: '',
                                          month: '',
                                          year: ''
                                      }
                                    : isoStringToDateObject(passengerDocumentDetails?.[input] as string) || {
                                          day: '',
                                          month: '',
                                          year: ''
                                      }
                            } else {
                                if (
                                    documentType === 'NATIONALID' &&
                                    (input === 'countryIssued' || input === 'nationality')
                                ) {
                                    acc[input] = 'Saudi Arabia' //for national id  we will show always default value as saudi arabia not read from BE
                                } else if (
                                    documentType === 'IQAMA' &&
                                    (input === 'countryIssued' || input === 'nationality')
                                ) {
                                    acc[input] = filledWithLocalSavedData
                                        ? targetSavedTraveler?.documentDetails?.[documentType]?.[input]
                                        : null //for IQAMA  we will show always default value as NULL because BE sending country code instead of country name
                                } else {
                                    acc[input] = filledWithLocalSavedData
                                        ? targetSavedTraveler?.documentDetails?.[documentType]?.[input]
                                        : passengerDocumentDetails?.[input] || ''
                                }
                            }
                            return acc
                        },
                        {} as Record<keyof DocumentDetails, any>
                    )

                    return acc
                },
                {} as Record<DocumentType, DocumentDetails>
            ) as Record<DocumentType, DocumentDetails>

            const preSelectedDocumentType = passenger?.docDetails?.at(0)?.docType
            traveler.selectedDocumentType =
                filledWithLocalSavedData &&
                targetSavedTraveler?.selectedDocumentType &&
                requiredDocuments?.includes(targetSavedTraveler?.selectedDocumentType)
                    ? targetSavedTraveler?.selectedDocumentType
                    : preSelectedDocumentType && requiredDocuments?.includes(preSelectedDocumentType)
                      ? preSelectedDocumentType
                      : requiredDocuments?.at(0)
            return traveler
        })
    }, [passengers, mandatoryFields])

    const [travelers, setTravelers] = useState<Traveler[]>(initialTravelersState)

    const onChangeTravelerDetails: PersonalDetailsFormProps['onChangeTravelerDetails'] = useCallback(
        (idx, field, value) => {
            setTravelers(prev => {
                const newTravelers = [...prev]
                const currentTraveler = newTravelers[idx]
                if (!currentTraveler) return prev
                const isString = typeof value === 'string'
                const updatedTraveler: Traveler = { ...currentTraveler, [field]: isString ? value.trim() : value }

                if (
                    field === 'selectedDocumentType' &&
                    value === 'NATIONALID' &&
                    !isNationalityRequiredToRenderInUserPersonalDetailsSection
                ) {
                    const inputs = TravelerDocumentUtils.travelerDocumentToInputsSegregate(value)
                    if (inputs.nationality) {
                        if (!updatedTraveler.documentDetails[value as DocumentType].nationality) {
                            updatedTraveler.documentDetails[value as DocumentType].nationality = 'Saudi Arabia'
                        }
                    }
                    if (inputs.countryIssued) {
                        if (!updatedTraveler.documentDetails[value as DocumentType].countryIssued) {
                            updatedTraveler.documentDetails[value as DocumentType].countryIssued = 'Saudi Arabia'
                        }
                    }
                }

                newTravelers[idx] = updatedTraveler
                return newTravelers
            })
        },
        [isNationalityRequiredToRenderInUserPersonalDetailsSection]
    )

    // New function to handle document field changes specifically
    const onChangeDocumentField = useCallback(
        (travelerIndex: number, documentType: DocumentType, field: string, value: any) => {
            setTravelers(prev => {
                const newTravelers = [...prev]
                const currentTraveler = newTravelers[travelerIndex]
                if (!currentTraveler) return prev
                const isString = typeof value === 'string'

                newTravelers[travelerIndex] = {
                    ...currentTraveler,
                    documentDetails: {
                        ...currentTraveler.documentDetails,
                        [documentType]: {
                            ...currentTraveler.documentDetails[documentType],
                            [field]: isString ? value.trim() : value
                        }
                    }
                }
                return newTravelers
            })
        },
        []
    )

    return {
        travelers,
        setTravelers,
        onChangeTravelerDetails,
        onChangeDocumentField,
        travelersIndexToPassengerTypeAndSerialNumberMap
    }
}
